import React, { useEffect, useRef, useState } from "react";
import { Crown, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const IMG = "/img/exc_scene";
const API = process.env.REACT_APP_BACKEND_URL;
const rand = (a, b) => a + Math.random() * (b - a);

// Hovering bats — each bobs in place; tap/click one and it teleports
// somewhere else on the page.
const BATS = [
  { id: "b-spread", src: `${IMG}/a4.webp`, w: 75, fx: 0.48, fy: 0.08 },
  { id: "b-armsup", src: `${IMG}/a5.webp`, w: 58, fx: 0.6, fy: 0.34 },
  { id: "b-dive", src: `${IMG}/a6.webp`, w: 48, fx: 0.22, fy: 0.45 },
  { id: "b-swoop", src: `${IMG}/a7.webp`, w: 58, fx: 0.82, fy: 0.5 },
  { id: "b-corner", src: `${IMG}/a8.webp`, w: 48, fx: 0.9, fy: 0.1 },
];

export const ExcourtierScene = () => {
  const { token } = useAuth();
  const batsRef = useRef(null);
  const rafRef = useRef(null);
  const [scared, setScared] = useState(() => parseInt(localStorage.getItem("exc_bats_scared") || "0", 10));
  const [copied, setCopied] = useState(false);
  const [board, setBoard] = useState(null);
  const pendingRef = useRef(0);
  const flushTimerRef = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const flushScares = () => {
    const delta = pendingRef.current;
    if (!delta || !tokenRef.current) return;
    pendingRef.current = 0;
    fetch(`${API}/api/bats/scare`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenRef.current}` },
      credentials: "include",
      body: JSON.stringify({ delta }),
    }).catch(() => { pendingRef.current += delta; });
  };

  const toggleBoard = async () => {
    if (board) { setBoard(null); return; }
    flushScares();
    try {
      const res = await fetch(`${API}/api/bats/leaderboard`);
      const data = await res.json();
      setBoard(data.leaderboard || []);
    } catch {
      setBoard([]);
    }
  };

  if (batsRef.current === null) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    batsRef.current = BATS.map((b) => ({
      ...b,
      x: b.fx * w, y: b.fy * h,
      bobF: rand(0.0012, 0.002), bobP: rand(0, Math.PI * 2),
      bobAmp: rand(8, 16), swayAmp: rand(4, 9),
      flip: Math.random() < 0.5 ? -1 : 1,
      popUntil: 0,
      el: null,
    }));
  }

  const teleport = (i) => {
    const b = batsRef.current[i];
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    b.x = rand(10, Math.max(20, vw - b.w - 10));
    b.y = rand(70, Math.max(120, vh - b.w - 160));
    b.flip = Math.random() < 0.5 ? -1 : 1;
    b.popUntil = performance.now() + 220;
    setScared((n) => {
      const next = n + 1;
      localStorage.setItem("exc_bats_scared", String(next));
      return next;
    });
    pendingRef.current += 1;
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushScares, 1500);
  };

  const shareTally = async () => {
    const text = `I've scared ${scared} bat${scared === 1 ? "" : "s"} on the Excourtier page 🦇 ${window.location.origin}/brands/excourtier`;
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    } catch {
      /* user dismissed share sheet */
    }
  };

  useEffect(() => {
    const step = (now) => {
      batsRef.current.forEach((b) => {
        if (!b.el) return;
        const bobY = Math.sin(now * b.bobF + b.bobP) * b.bobAmp;
        const swayX = Math.cos(now * b.bobF * 0.7 + b.bobP) * b.swayAmp;
        const pop = now < b.popUntil ? 0.3 : 1;
        b.el.style.transform = `translate3d(${b.x + swayX}px, ${b.y + bobY}px, 0) scaleX(${b.flip}) scale(${pop})`;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className="exc-scene" data-testid="excourtier-scene">
      <div className="exc-bat-layer">
        {BATS.map((b, i) => (
          <div
            key={b.id}
            className="exc-bat exc-bat-tappable"
            style={{ width: b.w }}
            ref={(el) => { if (batsRef.current[i]) batsRef.current[i].el = el; }}
            onPointerDown={() => teleport(i)}
            data-testid={`exc-bat-${b.id}`}
          >
            <img src={b.src} alt="" draggable={false} />
          </div>
        ))}
      </div>
      {scared > 0 && (
        <button
          type="button"
          className="exc-bat-tally"
          onClick={shareTally}
          title="Tap to share your tally"
          data-testid="exc-bat-tally"
        >
          {copied ? "copied!" : `${scared} bat${scared === 1 ? "" : "s"} scared`}
        </button>
      )}
      <button
        type="button"
        className="exc-bat-crown"
        onClick={toggleBoard}
        title="Bat-scarer leaderboard"
        data-testid="exc-bat-crown"
      >
        <Crown size={15} />
      </button>
      {board && (
        <div className="exc-bat-board" data-testid="exc-bat-board">
          <div className="exc-bat-board-head">
            <span><Crown size={13} /> TOP BAT-SCARERS</span>
            <button type="button" onClick={() => setBoard(null)} data-testid="exc-bat-board-close"><X size={14} /></button>
          </div>
          {board.length === 0 ? (
            <p className="exc-bat-board-empty">No member has scared a bat yet. The crown is up for grabs.</p>
          ) : (
            <ol>
              {board.map((r, i) => (
                <li key={r.name + i}>
                  <span className="exc-bat-board-name">{i === 0 && <Crown size={12} className="exc-bat-board-first" />}{r.name}</span>
                  <span className="exc-bat-board-count">{r.count}</span>
                </li>
              ))}
            </ol>
          )}
          {!token && <p className="exc-bat-board-note">Log in and your scares count toward the crown.</p>}
        </div>
      )}
    </div>
  );
};
